import React from 'react'

import { theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  AI_ASSISTANT_CLOSE_BUTTON,
  AI_ASSISTANT_EXPAND_BUTTON,
  AI_ASSISTANT_MINIMIZE_BUTTON,
} from 'utils/testing/testIDs'

const HeaderBackground = styled.div<{ isMinimized: boolean }>`
  background-color: ${theme.colors.primaryWhite};
  height: ${(props) => (props.isMinimized ? '60px' : '70px')};
  padding: ${(props) => (props.isMinimized ? '10px' : '20px')};
  display: flex;
  border-bottom: 2px solid ${theme.colors.primaryIvory};
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h6`
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  padding: 0;
`

const ButtonContainer = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  position: relative;
  top: -2px;
`

const ButtonBackground = styled.button`
  align-items: center;
  background-color: ${theme.colors.primaryIvory};
  border-radius: 50%;
  border: none;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 30px;
`

const CloseButton = styled(AcornsIcon)`
  path {
    stroke: ${theme.colors.primarySlate};
    stroke-width: 0.5px;
  }
`

const MinusButton = styled(AcornsIcon)`
  path {
    fill: ${theme.colors.primarySlate};
  }
`

const ExpandButton = styled(AcornsIcon)`
  path {
    fill: ${theme.colors.primarySlate};
  }
`

type Props = {
  setShowAIAssistant: (showAIAssistant: boolean) => void
  endChatSession: () => void
  isMinimized: boolean
  setIsMinimized: (isMinimized: boolean) => void
}

export const MessagesHeader = ({
  setShowAIAssistant,
  endChatSession,
  isMinimized,
  setIsMinimized,
}: Props) => {
  const { getRemoteString } = useRemoteStrings()
  const handleClose = () => {
    setShowAIAssistant(false)
    endChatSession()
    setIsMinimized(false)
  }

  const handleMinimize = () => {
    setIsMinimized(true)
  }

  const handleExpand = () => {
    setIsMinimized(false)
  }

  return (
    <HeaderBackground isMinimized={isMinimized}>
      <Title>
        {getRemoteString({
          key: 'acorns_intelligence.chat.title',
        })}
      </Title>
      <ButtonContainer>
        {isMinimized ? (
          <ButtonBackground
            onClick={handleExpand}
            data-testid={AI_ASSISTANT_EXPAND_BUTTON}
          >
            <ExpandButton
              height="17px"
              icon={AcornsIcon.Icon.Expand}
              width="17px"
            />
          </ButtonBackground>
        ) : (
          <ButtonBackground
            onClick={handleMinimize}
            data-testid={AI_ASSISTANT_MINIMIZE_BUTTON}
          >
            <MinusButton
              height="12px"
              icon={AcornsIcon.Icon.InlineMinus}
              width="12px"
            />
          </ButtonBackground>
        )}
        <ButtonBackground
          onClick={handleClose}
          data-testid={AI_ASSISTANT_CLOSE_BUTTON}
        >
          <CloseButton
            height="11px"
            icon={AcornsIcon.Icon.CloseXIcon}
            width="11px"
          />
        </ButtonBackground>
      </ButtonContainer>
    </HeaderBackground>
  )
}
